import { formatDate } from '@angular/common';
import { AfterViewInit, Component, ComponentRef, computed, inject, Input, input, LOCALE_ID, OnDestroy, Signal, signal, TemplateRef, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CalendarOptions, DatesSetArg, EventClickArg, FormatterInput } from '@fullcalendar/core';
import enLocale from '@fullcalendar/core/locales/en-gb';
import itLocale from '@fullcalendar/core/locales/it';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, firstValueFrom, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { sha1 } from 'js-sha1';

import { CalendarEvent, CalendarFilterGroup, CalendarToolbarConfig, CalendarViewGroup, CalendarViewLabel, CalendlySettings, Course, environment, EventsSearchFilterKey, EventsSearchParam, EventsSearchParams, OptionValue, PopupComponent, SearchFilterValue } from 'src/index';
import { AuthenticationService, CoursesService, EventsService, LoadingService, ModalOptions, PopupService } from 'src/index/services.index';

declare var Calendly: any;

@Component({
  selector: 'meta-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css']
})
export class CalendarComponent<T extends CalendarViewLabel> implements AfterViewInit, OnDestroy {
  popupService = inject(PopupService);
  translateService = inject(TranslateService);
  locale = inject(LOCALE_ID);
  loadingService = inject(LoadingService);
  coursesService = inject(CoursesService);
  eventsService = inject(EventsService);
  authService = inject(AuthenticationService);
  router = inject(Router);

  private courseCache = new Map<string, Observable<Course>>();
  private resizeObserver: ResizeObserver | undefined;

  private config = { ...environment.calendarModule };
  coursesPreviewConfig = {
    ...environment.calendarModule?.coursesPreviewConfig,
  };
  showFilterByMyCourses = this.config.showFilterByMyCourses;
  $isMobile = signal(window.innerWidth < (this.config?.mobileBreakpoint ?? 900));
  $isMobileSingleView = signal(window.innerWidth < (this.config?.singleViewBreakpoint ?? 500));
  private $customButtonLabel = computed(() => this.$isMobile() ? undefined : this.translateService.instant('Refine Search'));
  $showMyCourses = signal(false);

  private eventDetailRef: ComponentRef<PopupComponent> | undefined;
  private calendarFiltersRef: ComponentRef<PopupComponent> | undefined;

  @ViewChild('eventTemplate') eventTemplate!: TemplateRef<any>;
  @ViewChild('filtersTemplate') filtersTemplate!: TemplateRef<any>;
  @ViewChild('eventIsScheduledModalTemplate') eventIsScheduledModalTemplate!: TemplateRef<any>;
  @ViewChild('eventIsStartingModalTemplate') eventIsStartingModalTemplate!: TemplateRef<any>;
  @ViewChild('calendar', { static: false }) calendarRef!: any;

  @Input() toolbarSetup?: CalendarToolbarConfig;
  @Input() hideFilterButton?: boolean;

  public $inputDesktopLabel = input.required<T | undefined>({ alias: 'desktopLabel' });
  public $inputMobileLabel = input.required<T | undefined>({ alias: 'mobileLabel' });
  private $updatedView = signal<string | undefined>(undefined);
  private $view: Signal<string> = computed(() => {
    if (this.$updatedView()) {
      return this.$updatedView()!;
    }
    if (this.$isMobile() && this.$inputMobileLabel()) {
      return this.eventsService.getCalendarViewFromLabel(this.$inputMobileLabel()!);
    }
    return this.eventsService.getCalendarViewFromLabel(this.$inputDesktopLabel() || 'weekLabel');
  });

  $rightCalendarToolbarSetup = computed(() => {
    if (!this.hideFilterButton) {
      if (this.config.customViewConfig?.toolbarConfig(this.$isMobile())[this.toolbarSetup || 'primary'].right) {
        return this.config.customViewConfig?.toolbarConfig(this.$isMobile())[this.toolbarSetup || 'primary'].right + ' refineSearchCustom';
      } else {
        return 'refineSearchCustom';
      }
    } else if (this.config.customViewConfig?.toolbarConfig(this.$isMobile())[this.toolbarSetup || 'primary'].right) {
      return this.config.customViewConfig?.toolbarConfig(this.$isMobile())[this.toolbarSetup || 'primary'].right;
    }
    return;
  })

  $fixedSearch = input<EventsSearchParam[]>([], { alias: 'fixedSearch' });
  $searchParams = signal<EventsSearchParams>({});
  $currentSearch = computed<EventsSearchParams>(() => ({
    ...this.$searchParams(),
    rangeStart: formatDate(this.$currentDateRange()?.start ?? new Date(), 'yyyy-MM-dd', this.locale),
    rangeEnd: formatDate(this.$currentDateRange()?.end ?? new Date(), 'yyyy-MM-dd', this.locale),
    myCourses: this.$showMyCourses(),
    ...this.$fixedSearch().reduce((search, item) => ({
      ...search,
      [item.facetField as EventsSearchFilterKey]: item.value,
    }), {} as EventsSearchParams),
  }))

  currentEventsResults$ = toObservable(this.$currentSearch).pipe(
    tap(() => this.loadingService.show()),
    switchMap(searchParams => this.eventsService.getEvents(this.config?.facetField ?? '', searchParams)),
    shareReplay(1),
    tap(() => this.loadingService.hide()),
  );
  currentEvents$ = this.currentEventsResults$.pipe(
    map(({ events }) => this.mapEventsStatus(events)),
  );
  $events = toSignal(this.currentEvents$);
  $calendarEvents = computed(() => {
    const events = this.$events() ?? [];
    return events.map(event => ({
      id: event.id,
      start: event.dateStart ? new Date(event.dateStart) : undefined,
      end: event.dateEnd ? new Date(event.dateEnd) : undefined,
      title: event.title,
      backgroundColor: event.color,
      extraParams: {
        rec: event.rec,
        date: event.date,
        color: event.color,
        url: event.url,
      }
    }));
  })
  $currentFacets = toSignal(this.currentEventsResults$.pipe(
    map(({ facets }) => facets),
    tap((facets => {
      const facetField = this.config.facetField;
      if (facetField && facets && facets[facetField]) {
        let values = facets[facetField] ?? [];
        facets[facetField] = values.map(facet => ({
          ...facet,
          color: this.eventsService.getColorForEvent(`${facet.value ?? ''}`)
        }));
      }
    })),
    distinctUntilChanged(),
    shareReplay(1),
  ));

  public $searchFilterGroup: Signal<CalendarFilterGroup | undefined> = computed(() => {
    const facetField = this.config.facetField;
    const facets = this.$currentFacets();
    const searchParams = this.$searchParams();
    if (facetField && facets && facets[facetField]) {
      let values = facets[facetField] ?? [];
      return {
        id: facetField,
        type: 'checkbox',
        values,
        label: `Label_${facetField}`,
        selectedValue: searchParams[facetField],
      } as CalendarFilterGroup;
    }
    return undefined;
  });

  public $searchViewGroup: Signal<CalendarViewGroup | undefined> = computed(() => {
    const currentView = this.$view();

    if (!this.$isMobileSingleView() && currentView) {
      let values: SearchFilterValue[] = [{
        label: this.translateService.instant('Week'),
        value: this.eventsService.getCalendarViewFromLabel('weekLabel'),
      }, {
        label: this.translateService.instant('Month'),
        value: this.eventsService.getCalendarViewFromLabel('monthLabel'),
      }, {
        label: this.translateService.instant('List'),
        value: this.eventsService.getCalendarViewFromLabel('listLabel'),
      }];
      return {
        id: currentView,
        type: 'checkbox',
        values,
        selectedValue: currentView,
      } as CalendarViewGroup;
    }
    return undefined;
  });

  private $currentDateRange = signal<{ start: Date, end: Date } | undefined>(undefined);

  $renderViews = input(undefined, { alias: 'renderViews' });
  $titleFormat = signal<undefined | FormatterInput>(undefined)

  private $onToggleFilter = signal<string[] | undefined>(undefined);
  private onLangChange$ = this.translateService.onLangChange.pipe(
    map(change => change.lang),
    distinctUntilChanged(),
    shareReplay(1),
  );

  private $lang = toSignal(this.onLangChange$);
  private $localeInput = computed(() => {
    switch (this.$lang() || this.translateService.currentLang) {
      case 'en':
        return enLocale;
      default:
        return itLocale;
    }
  });

  public $calendarOptions = computed(() => {
    const view = this.$view();
    const lang = this.$lang();
    const calendarOptions: CalendarOptions = {
      locale: this.$localeInput(),
      plugins: [
        interactionPlugin,
        dayGridPlugin,
        timeGridPlugin,
        listPlugin,
      ],
      titleFormat: this.$titleFormat(),
      headerToolbar: {
        ...this.config.customViewConfig?.toolbarConfig(this.$isMobile())[this.toolbarSetup || 'primary'],
        right: this.$rightCalendarToolbarSetup(),
      },
      initialView: view,
      views: this.config.customViewConfig?.customViews(lang || this.translateService.currentLang),
      weekends: this.config.customViewConfig?.includeWeekends,
      editable: this.config.customViewConfig?.editable,
      selectable: this.config.customViewConfig?.selectable,
      selectMirror: true,
      dayMaxEvents: true,
      noEventsClassNames: 'text-red-600 font-semibold text-[1.4rem]',
      height: '85vh',
      allDaySlot: false,
      nowIndicator: true,
      businessHours: [
        {
          daysOfWeek: [1, 2, 3, 4, 5, 6],
          startTime: '08:00',
          endTime: '18:00'
        },
      ],
      customButtons: {
        refineSearchCustom: {
          click: () => {
            this.calendarFiltersRef = this.popupService.openModal(this.filtersTemplate, {
              closable: true,
              size: 'sidebar-tall',
              position: 'right',
              closeOnEsc: true,
              modalConfig: {
                header: {
                  title: this.$customButtonLabel(),
                },
                footer: {
                  showTopBorder: false,
                  hideCancel: true,
                  hideConfirm: true,
                },
              },
            });
          }
        }
      },
      eventMouseEnter: (mouseEnterInfo) => {
        mouseEnterInfo.jsEvent.preventDefault();
        mouseEnterInfo.jsEvent.stopPropagation();
      },
      eventClick: (clickInfo) => {
        clickInfo.jsEvent.preventDefault();
      },
      datesSet: (arg: DatesSetArg) => {
        this.$onToggleFilter.set(undefined);
        this.$currentDateRange.set({
          start: arg.start,
          end: arg.end,
        });
      }
    };

    return calendarOptions;
  });

  $studyPathCourses = toSignal(this.coursesService.getStudyPathCourses().pipe(
    map(courses => courses || []),
    shareReplay(1),
  ));

  public $selectedEvent = signal<CalendarEvent | undefined>(undefined);
  selectedEventCourse$ = toObservable(this.$selectedEvent).pipe(
    map(event => event?.courseId),
    switchMap(courseId => courseId ? this.getCourseById(courseId) : of(undefined)),
    map((course) => {
      if(!course) {
        return
      }

      const courseInStudyPath = this.$studyPathCourses()?.find(studyPathCourse => studyPathCourse.uuid === course.uuid);
      const associatedToUser = !!courseInStudyPath;

      return {...course, associatedToUser }
    }),
    shareReplay(1),
  )
  $selectedEventCourse = toSignal(this.selectedEventCourse$);
  $courseToPreview = signal<Course | undefined>(undefined);

  ngAfterViewInit() {

    if (this.calendarRef) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const rect = entry.contentRect;

          if (rect.width < (this.config?.singleViewBreakpoint ?? 500)) {
            this.$isMobileSingleView.set(true);
            this.$titleFormat.set({
              month: 'short',
              year: '2-digit',
            });
            this.calendarRef.calendar.changeView(this.$inputMobileLabel());
          } else {
            this.$isMobileSingleView.set(false);
            this.$titleFormat.set(undefined);
            if (this.config.resetViewOnResize) {
              this.calendarRef.calendar.changeView(this.$view());
            }
          }

          if (rect.width < (this.config?.mobileBreakpoint ?? 900)) {
            this.$isMobile.set(true);
          } else {
            this.$isMobile.set(false);
          }
        }
      });

      this.resizeObserver.observe(this.calendarRef.element.nativeElement);
    }

    if (this.$isMobile()) {
      this.$updatedView.set(this.eventsService.getCalendarViewFromLabel(this.$inputMobileLabel() || 'listLabel'));
    } else {
      this.$updatedView.set(undefined);
    }

    if ((window as any).Calendly && !this.config.disableCalendly) {
      firstValueFrom(this.eventsService.getCalendly()).then(settings => {
        if (settings && settings.url) {
          (window as any).Calendly.initBadgeWidget({
            url: settings.url,
            text: settings.text || 'Fissa appuntamento con Calendly',
            color: this.config.calendlyDefault?.bgColor || settings.color || '#0069ff',
            textColor: this.config.calendlyDefault?.textColor || settings.textColor || '#ffffff',
          });
        }
      })
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if ((window as any).Calendly) {
        (window as any).Calendly.destroyBadgeWidget();
    }
  }

  clickOnEvent(clickInfo: EventClickArg) {
    const events = this.$events() ?? [];
    const event = events.find(event => event.id === clickInfo.event.id);
    this.$selectedEvent.set(event);
    this.eventDetailRef = this.popupService.openModal(this.eventTemplate, {
      closable: true,
      size: 'medium',
      closeOnEsc: true,
      callbacks: {
        cancel: () => this.eventDetailRef = undefined,
      },
      modalConfig: {
        header: {
          title: clickInfo.event.title,
        },
        footer: {
          showTopBorder: false,
          hideCancel: true,
          hideConfirm: true,
        },
      },
    });
  };

  updateActiveFilters(value: OptionValue) {
    const filter = this.$searchFilterGroup();
    if (filter) {
      filter.selectedValue = value;
      this.$searchParams.set({
        [filter.id]: value,
      });
    }
    if (this.calendarFiltersRef) {
      this.calendarFiltersRef.instance.doClose();
    }
  }

  updateCalendarView(value: OptionValue) {
    this.calendarRef.calendar.changeView(value);
    this.$updatedView.set(value as CalendarViewLabel);
    if (this.calendarFiltersRef) {
      this.calendarFiltersRef.instance.doClose();
    }
  }

  mapEventsStatus(events: CalendarEvent[]) {
    return events.map((event, index) => {
      const today = new Date();
      const start = event.dateStart ? new Date(event.dateStart) : undefined;
      const end = event.dateEnd ? new Date(event.dateEnd) : undefined;
      let passed = end && today > end;
      let future = start && today <= start;
      let inProgress = false;
      if (start && end) {
        inProgress = start <= today && today < end;
      } else if (start) {
        inProgress = start <= today;
      }
      let date = '';
      if (start) {
        date = formatDate(start, 'dd/MM/yyyy', this.locale);
      }

      let eventUrl = undefined;
      if (event.url) {
        eventUrl = event.url;
        if (eventUrl.indexOf('USERNAME') >= 0) {
          const urlObj = new URL(eventUrl);
          const BASE_PATH = `${urlObj.origin}${urlObj.pathname}`;
          const api = urlObj.pathname.split('/').pop();
          const params = `meetingID=${urlObj.searchParams.get('meetingID')}&fullName=${this.authService.$currentUser()?.firstName}+${this.authService.$currentUser()?.lastName}&password=${urlObj.searchParams.get('password')}&redirect=true&joinViaHtml5=false`;
          const key = document.querySelector('meta-contents-viewer-root[data-bbb-key]')?.getAttribute('data-bbb-key');
          if (key) {
            const stringToHash = `${api}${params}${key}`;
            const checksum = sha1(stringToHash);
            eventUrl = `${BASE_PATH}?${params}&checksum=${checksum}`;
          } else {
            eventUrl = undefined;
          }
        }
      }

      return {
        ...event,
        url: eventUrl,
        date,
        passed,
        future,
        inProgress,
        color: this.eventsService.getColorForEvent(this.eventsService.getPropForColorMapping(event) ?? ''),
        id: event.id || index.toString(), //TODO, gestire id univoci eventi
      }
    })
  }

  getCourseById(courseId: string): Observable<Course> {
    if (!this.courseCache.has(courseId)) {
      const request = this.coursesService.getCourse(courseId).pipe(map(course => course!), shareReplay(1),);
      this.courseCache.set(courseId, request);
    }
    return this.courseCache.get(courseId)!;
  }

  getCalendarViewFromLabel(label: CalendarViewLabel) {
    return this.eventsService.getCalendarViewFromLabel(label);
  }

  updateCourseToPreview(course: Course) {
    this.$courseToPreview.set({ ...course });
  }

  toggleShowMyCourses() {
    this.$showMyCourses.update(flag => !flag);
  }

  async startCourse(course: Course, urlToOpen?: string | void) {
    if (this.eventDetailRef) {
      this.eventDetailRef.instance.doClose();
    }
    if (urlToOpen) {
      return this.router.navigateByUrl(urlToOpen);
    }
    try {
      await this.coursesService.openCourse(course);
    } catch (error) {
      console.error(error);
    }
    return;
  }

  onParticipateClick($event: MouseEvent, force = false) {
    const selectedEvent = this.$selectedEvent();

    if (!selectedEvent) {
      return;
    }

    const { dateStart } = selectedEvent;

    if (!dateStart) {
      return;
    }

    const dateStartObj = new Date(dateStart);
    const aQuarterToStartDate = new Date(
      dateStartObj.getTime() - 15 * 60 * 1000,
    );

    const now = new Date();
    const commonModalOptions: ModalOptions = {
      closable: true,
      size: 'small',
      position: 'center-center',
      closeOnEsc: true,
      modalConfig: {
        footer: {
          showTopBorder: false,
          hideCancel: true,
          hideConfirm: true,
        },
      },
    };

    if (!force) {
      if (now < aQuarterToStartDate) {
        $event.preventDefault();

        const eventIsScheduledModal = this.popupService.openModal(
          this.eventIsScheduledModalTemplate,
          {
            ...commonModalOptions,
            modalConfig: {
              ...commonModalOptions.modalConfig,
              header: { title: 'Event scheduled' },
            },
          },
        );
      } else if (aQuarterToStartDate < now && now < dateStartObj) {
        $event.preventDefault();

        const eventIsStartingModal = this.popupService.openModal(
          this.eventIsStartingModalTemplate,
          {
            ...commonModalOptions,
            modalConfig: {
              ...commonModalOptions.modalConfig,
              header: { title: 'Event will soon begin' },
            },
          },
        );
      } else {
        // do nothing - event will emit and the link will be opened on a new page
      }
    }

  }
}
